import { useEffect } from 'react';
import { GetServerSideProps } from 'next';

import { useSearchParams } from 'next/navigation';
import AuthoredPageRoutes from '@/components/authored-page-routes/authored-page-routes';
import { BFF_PATH, PLP_PAGE_TYPE, banners } from '@/data/constants';
import { PreRenderProps } from '@/pages/_app';
import { getPlpViewDefinition } from '@/ssr/view-definition-plp-page-query';
import { SRPFeedbackProvider, useSRPRelevanceFeedback } from '@iceberg/srp-relevance-feedback';
import { useCommonContextProps, useDetermineBanner } from '@/hooks';

import { fetchDataAndHandleErrors, measureDuration, withTokenRefresh } from '@/utils/ssr-utils';
import { ANALYTICS_PAGE_NAMES } from '@/utils/analytics/constants';
import { ANALYTICS_EVENT_NAMES, collectMetrics, type IApiTiming } from '@iceberg/report-perfect-experience-analytics';
import { PageProps } from '..';
import { getKeyIdFromUrl } from '@/utils';

export const getServerSideProps: GetServerSideProps<PreRenderProps> = withTokenRefresh(async (context: any) => {
  const { result: data, duration: ssrFetchDurationMs } = await measureDuration(() =>
    fetchDataAndHandleErrors(context, getPlpViewDefinition, PLP_PAGE_TYPE.SEARCH)
  );
  const { resolvedUrl } = context;
  const keyId = getKeyIdFromUrl(resolvedUrl);
  const ssrFetchData: IApiTiming = {
    name: `POST ${BFF_PATH.SEARCH}${keyId}`,
    duration: ssrFetchDurationMs,
  };

  // Do not change PageProps type dramatically (only add new props)
  // Some SDUI components (Masthead) reads the props manually via NextData in the DOM, not via component props.
  // Changing the type will break header rendering, specifically the bagCount and favourites.
  const props: PageProps = {
    ...data,
    ssrFetchData,
  };
  return { props };
});

const SRPRelevanceFeedbackInitializer = () => {
  useSRPRelevanceFeedback();
  return null;
};

const SearchResultRoutes = (props: any) => {
  const pageName = ANALYTICS_PAGE_NAMES.SRP;
  collectMetrics(ANALYTICS_EVENT_NAMES.PAGE_INIT, {
    detail: {
      page_name: pageName,
    },
  });
  useEffect(() => {
    collectMetrics(ANALYTICS_EVENT_NAMES.PAGE_READY, {
        detail: {
          page_name: pageName,
          page_id: pageName,
        }
      }); 
  }, []);
  const { isSrpFeedbackEnabled } = useCommonContextProps();
  const isPharmaprix = useDetermineBanner();
  const banner = isPharmaprix ? banners.PHARMAPRIX : banners.SHOPPERS;
  const searchParams = useSearchParams();
  const urlSearchParams = searchParams ? new URLSearchParams(searchParams.toString()) : null;

  return (
    <SRPFeedbackProvider
      enabled={Boolean(isSrpFeedbackEnabled)}
      bannerDomain={`${banner}.ca`}
      searchParams={urlSearchParams}
    >
      <AuthoredPageRoutes {...props} />
      <SRPRelevanceFeedbackInitializer />
    </SRPFeedbackProvider>
  );
};

export default SearchResultRoutes;
